import { useParams } from "react-router-dom";
import { useMemo, useCallback } from "react";
import useFeature from "./useFeature";
import { useGetFeatureSpectraQuery } from "../featuresSlice";
import useSearchParams from "../../../hooks/useSearchParams";
import usePaging from "../../../hooks/usePaging";
import useLang from "../../../hooks/useLang";
import paramConfig from "../../../config/paramConfig";

const DROPDOWN_TO_API_LABEL = {
  positives: "positive",
  negatives: "negative",
  unassigned: "unassigned",
};

const useFeatureSpectrumBlock = () => {
  const { getParam } = useSearchParams();
  const { activePage } = usePaging({});
  const lang = useLang();
  const selectedSpectraType =
    getParam(paramConfig.EXAMPLE) || paramConfig.DEFAULT_EXAMPLE;
  const apiLabel = DROPDOWN_TO_API_LABEL[selectedSpectraType];
  const { featureId } = useParams();
  const { data: featureData } = useFeature(
    { id: featureId },
    { skip: featureId == null }
  );
  const link = useCallback((rowData) => `/spectra/${rowData}`, []);

  const { data: spectraData, isFetching } = useGetFeatureSpectraQuery(
    { id: featureId, label: apiLabel, page: activePage },
    { skip: featureId == null || apiLabel == null }
  );

  const rows = useMemo(() => {
    if (spectraData?.response) return spectraData.response;
    if (apiLabel === "positive" && Array.isArray(featureData?.spectrum_ids)) {
      return featureData.spectrum_ids.map((id) => ({
        spectrum: { id },
        value: 1,
        last_edit_at: null,
      }));
    }
    return [];
  }, [spectraData?.response, featureData?.spectrum_ids, apiLabel]);

  const items = useMemo(() => {
    if (isFetching) {
      return [
        {
          id: "loading",
          label: false,
          value: lang("spectra.spectra_loading"),
        },
      ];
    }
    if (rows.length === 0) {
      return [
        {
          id: "emptyLabel",
          label: false,
          value: lang("spectra.empty_label"),
        },
      ];
    }
    return [
      {
        id: "spectraArrayHoverable",
        type: "spectraArrayHoverable",
        data: rows,
        featureId,
        link,
      },
      { id: "notes", formOnly: true },
    ];
  }, [isFetching, rows, featureId, link, lang]);

  return {
    items,
    pagination: spectraData?.pagination || null,
  };
};

export default useFeatureSpectrumBlock;
